const chapter = {
  id: 'mineral-nutrition',
  slug: 'mineral-nutrition',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Mineral Nutrition',
        hindi: 'खनिज पोषण',
  },
  estReadMinutes: 45,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '2 questions in NEET',
        hindi: 'NEET में 2 प्रश्न **हाइड्रोपोनिक्स** - जूलियस वॉन सैक्स (1860); परिभाषित पोषक तत्व समाधानों में उगाए गए पौधे → आवश्यक तत्वों की पहचान की गई।',
  },
  syllabusTags: ['Plant Physiology', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'essential-elements',
      title: {
        english: 'Essential Mineral Elements',
        hindi: 'आवश्यक खनिज तत्व',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Hydroponics** — Julius von Sachs (1860); plants grown in defined nutrient solutions → identified essential elements.',
        hindi: '**हाइड्रोपोनिक्स** - जूलियस वॉन सैक्स (1860); परिभाषित पोषक तत्व समाधानों में उगाए गए पौधे → आवश्यक तत्वों की पहचान की गई।',
            },
            {
              english: 'Of ~ 105 elements, only **17 essential** for plants.',
        hindi: '~105 तत्वों में से, पौधों के लिए केवल **17 आवश्यक**।',
            },
            {
              english: 'Criteria of essentiality (Arnon & Stout):',
        hindi: 'अनिवार्यता के मानदंड (अर्नोन और स्टाउट):',
            },
            {
              english: '• Absolutely necessary for growth & reproduction.',
        hindi: '• वृद्धि और प्रजनन के लिए बिल्कुल आवश्यक है।',
            },
            {
              english: '• Specific role; cannot be replaced by another.',
        hindi: '• विशिष्ट भूमिका; दूसरे द्वारा प्रतिस्थापित नहीं किया जा सकता.',
            },
            {
              english: '• Directly involved in plant metabolism.',
        hindi: '• सीधे पौधों के चयापचय में शामिल होता है।',
            },
            {
              english: 'Two groups by amount required:',
        hindi: 'आवश्यक राशि के अनुसार दो समूह:',
            },
            {
              english: '• **Macronutrients** (>10 mmol/kg dry wt) — **C, H, O, N, P, S, K, Ca, Mg** (9).',
        hindi: '• **मैक्रोन्यूट्रिएंट्स** (>10 mmol/kg शुष्क वजन) - **सी, एच, ओ, एन, पी, एस, के, सीए, एमजी** (9)।',
            },
            {
              english: '• **Micronutrients / Trace** (<10 mmol/kg) — **Fe, Mn, Cu, Zn, B, Mo, Cl, Ni** (8).',
        hindi: '• **सूक्ष्म पोषक तत्व / ट्रेस** (<10 mmol/kg) - **Fe, Mn, Cu, Zn, B, Mo, Cl, Ni** (8)।',
            },
            {
              english: 'Functions categorised: structural (C, H, O, N), energy-related (P, Mg in chlorophyll, ATP), enzyme activators (Mg²⁺, K⁺), redox (Fe, Cu).',
        hindi: 'वर्गीकृत कार्य: संरचनात्मक (सी, एच, ओ, एन), ऊर्जा से संबंधित (क्लोरोफिल में पी, एमजी, एटीपी), एंजाइम सक्रियकर्ता (एमजी²⁺, के⁺), रेडॉक्स (एफई, सीयू)।',
            },
          ],
        },
      ],
    },
    {
      id: 'role-deficiency',
      title: {
        english: 'Role & Deficiency Symptoms',
        hindi: 'भूमिका और कमी के लक्षण',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Element', 'Role', 'Deficiency'],
          rows: [
            ['N', 'Proteins, nucleic acids, chlorophyll', 'Chlorosis (older leaves), stunted growth'],
            ['P', 'ATP, NA, phospholipids', 'Dull green leaves, purple pigmentation'],
            ['K', 'Stomatal opening, enzyme activator, osmoregulation', 'Mottled chlorosis, marginal scorching, weak stem'],
            ['Ca', 'Cell wall (Ca-pectate in middle lamella), spindle formation, membrane', 'Death of meristems; deformed young leaves'],
            ['Mg', 'Centre of chlorophyll; activates RUBISCO, PEPcase', 'Interveinal chlorosis (older leaves first)'],
            ['S', 'Cysteine, methionine, vitamins (thiamine, biotin), CoA', 'Chlorosis of young leaves'],
            ['Fe', 'Cytochromes, ferredoxin; chlorophyll synthesis', 'Interveinal chlorosis (young leaves)'],
            ['Mn', 'Photolysis of water (PS-II); activator', '**Grey spots of oat** (classic symptom)'],
            ['Zn', 'Auxin (IAA) synthesis, alcohol dehydrogenase', 'Little leaf, white bud disease (maize)'],
            ['Cu', 'Plastocyanin, oxidases', 'Reclamation disease, die-back'],
            ['B', 'Carbohydrate translocation, pollen germination', '**Heart rot of beet** (classic)'],
            ['Mo', 'Nitrate reductase, nitrogenase', 'Whiptail of cauliflower'],
            ['Cl', 'PS-II water splitting, anion balance', 'Wilting, chlorotic leaves'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Mobile elements** (deficient symptoms appear in **older leaves first**) — N, P, K, Mg.',
        hindi: '**चल तत्व** (कमी के लक्षण **पुरानी पत्तियों** में सबसे पहले दिखाई देते हैं**) - एन, पी, के, एमजी।',
            },
            {
              english: '**Immobile** (deficient → **young leaves first**) — Ca, S, Fe, B, Cu.',
        hindi: '**स्थिर** (कमी → **पहले युवा पत्तियाँ**) - Ca, S, Fe, B, Cu।',
            },
            {
              english: '**Critical concentration** — minimum concentration of nutrient just below which growth slows.',
        hindi: '**महत्वपूर्ण सांद्रता** - पोषक तत्वों की न्यूनतम सांद्रता जिसके ठीक नीचे विकास धीमा हो जाता है।',
            },
            {
              english: '**Toxicity** — when concentration exceeds optimum (e.g. **Mn toxicity** induces deficiencies of Fe, Mg, Ca).',
        hindi: '**विषाक्तता** - जब एकाग्रता इष्टतम से अधिक हो जाती है (उदाहरण के लिए **एमएन विषाक्तता** Fe, Mg, Ca की कमी उत्पन्न करती है)।',
            },
          ],
        },
      ],
    },
    {
      id: 'mechanism-uptake',
      title: {
        english: 'Mechanism of Mineral Absorption',
        hindi: 'खनिज अवशोषण की क्रियाविधि',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Two phases:',
        hindi: 'दो चरण:',
            },
            {
              english: '• **Passive** — initial rapid; ions enter apoplast (free space) of root.',
        hindi: '• **निष्क्रिय** — प्रारंभिक तीव्र; आयन जड़ के एपोप्लास्ट (मुक्त स्थान) में प्रवेश करते हैं।',
            },
            {
              english: '• **Active** — slow; ions move across plasma membrane into symplast → require ATP; against gradient.',
        hindi: '• **सक्रिय** — धीमा; आयन प्लाज्मा झिल्ली के पार सिम्प्लास्ट में चले जाते हैं → एटीपी की आवश्यकता होती है; ढाल के विरुद्ध.',
            },
            {
              english: 'Specific membrane proteins (carriers/channels) regulate ion entry.',
        hindi: 'विशिष्ट झिल्ली प्रोटीन (carriers/channels) आयन प्रवेश को नियंत्रित करते हैं।',
            },
            {
              english: 'Endodermis (with **Casparian strip**) prevents back-flow of ions from xylem to soil.',
        hindi: 'एंडोडर्मिस (**कैस्पेरियन स्ट्रिप** के साथ) जाइलम से मिट्टी में आयनों के वापस प्रवाह को रोकता है।',
            },
            {
              english: 'Once in xylem, ions transported via **transpiration stream**; redistributed via phloem.',
        hindi: 'एक बार जाइलम में, आयनों को **वाष्पोत्सर्जन धारा** के माध्यम से ले जाया जाता है; फ्लोएम के माध्यम से पुनः वितरित।',
            },
          ],
        },
      ],
    },
    {
      id: 'nitrogen-cycle',
      title: {
        english: 'Nitrogen Metabolism & N-fixation',
        hindi: 'नाइट्रोजन उपापचय और N-स्थिरीकरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Atmospheric N₂ (78 %) is unavailable → must be **fixed**.',
        hindi: 'वायुमंडलीय N₂ (78%) अनुपलब्ध है → इसे **ठीक** किया जाना चाहिए। **जैविक एन-फिक्सेशन** - **एन-फिक्सिंग बैक्टीरिया** द्वारा: मुक्त-जीवित (***एज़ोटोबैक्टर, बेजरिनकिया, रोडोस्पिरिलम, बैसिलस, एनाबेना, नोस्टॉक***); सहजीवी (***राइजोबियम*** फलियों की जड़ की गांठों में; **फ्रैंकिया** गैर-फलियां में - *अलनस*)।',
            },
            {
              english: '**Biological N-fixation** — by **N-fixing bacteria**: free-living (***Azotobacter, Beijerinckia, Rhodospirillum, Bacillus, Anabaena, Nostoc***); symbiotic (***Rhizobium*** in legume root nodules; **Frankia** in non-legumes — *Alnus*).',
        hindi: '**जैविक एन-फिक्सेशन** - **एन-फिक्सिंग बैक्टीरिया** द्वारा: मुक्त-जीवित (***एज़ोटोबैक्टर, बेजरिनकिया, रोडोस्पिरिलम, बैसिलस, एनाबेना, नोस्टॉक***); सहजीवी (***राइजोबियम*** फलियों की जड़ की गांठों में; **फ्रैंकिया** गैर-फलियां में - *अलनस*)।',
            },
            {
              english: 'Reaction: $N_2 + 8 H^+ + 8 e^- + 16 ATP \\rightarrow 2 NH_3 + H_2 + 16 ADP + 16 Pi$.',
        hindi: 'प्रतिक्रिया: $N_2 + 8 H^+ + 8 e^- + 16 ATP \\rightarrow 2 NH_3 + H_2 + 16 ADP + 16 Pi$.',
            },
            {
              english: 'Catalysed by **nitrogenase enzyme complex** (Mo-Fe protein); extremely O₂-sensitive → protected by **leghaemoglobin** (pink/red O₂ scavenger in nodules).',
        hindi: '**नाइट्रोजनेज एंजाइम कॉम्प्लेक्स** (मो-फ़े प्रोटीन) द्वारा उत्प्रेरित; अत्यधिक O₂-संवेदनशील → **लेघहीमोग्लोबिन** द्वारा संरक्षित (pink/red O₂ नोड्यूल्स में स्वेवेंजर)।',
            },
            {
              english: 'Nodule formation steps: Rhizobium contact → curling of root hair → infection thread → nodule formation → bacteria become **bacteroids**.',
        hindi: 'गांठ गठन के चरण: राइजोबियम संपर्क → जड़ बालों का मुड़ना → संक्रमण धागा → गांठ गठन → बैक्टीरिया **बैक्टेरॉइड** बन जाते हैं।',
            },
            {
              english: '**Nitrification** — soil ammonia → nitrite (*Nitrosomonas, Nitrococcus*) → nitrate (*Nitrobacter*).',
        hindi: '**नाइट्रीकरण** — मृदा अमोनिया → नाइट्राइट (*नाइट्रोसोमोनास, नाइट्रोकोकस*) → नाइट्रेट (*नाइट्रोबैक्टर*)।',
            },
            {
              english: '**Denitrification** — nitrate → N₂ (*Pseudomonas, Thiobacillus*).',
        hindi: '**विनाइट्रीकरण** — नाइट्रेट → N₂ (*स्यूडोमोनास, थियोबैसिलस*)।',
            },
            {
              english: '**Ammonification** — decomposers convert organic N → NH₃.',
        hindi: '**अमोनीकरण** - डीकंपोजर कार्बनिक एन → एनएच₃ को परिवर्तित करते हैं।',
            },
            {
              english: 'Plants take up nitrate ($NO_3^-$) → reduced by **nitrate reductase (Mo-cofactor) → nitrite → ammonia → amino acids** (reductive amination, transamination).',
        hindi: 'पौधे नाइट्रेट ($NO_3^-$) लेते हैं → **नाइट्रेट रिडक्टेस (मो-कोफ़ेक्टर) → नाइट्राइट → अमोनिया → अमीनो एसिड** (रिडक्टिव एमिनेशन, ट्रांसएमिनेशन) द्वारा कम किया जाता है।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Essential elements: 17 (9 macro + 8 micro)}',
      desc: {
        english: '',
        hindi: 'आवश्यक तत्व: 17 (9 मैक्रो + 8 माइक्रो)',
      },
    },
    {
      latex: '\\text{Mobile (older leaf): N, P, K, Mg; Immobile (young leaf): Ca, S, Fe, B}',
      desc: {
        english: '',
        hindi: 'मोबाइल (पुराना पत्ता): एन, पी, के, एमजी; स्थिर (युवा पत्ता): Ca, S, Fe, B',
      },
    },
    {
      latex: '\\text{Mg = chlorophyll centre; Mn = photolysis (PS-II)}',
      desc: {
        english: '',
        hindi: 'एमजी = क्लोरोफिल केंद्र; एमएन = फोटोलिसिस (पीएस-II)',
      },
    },
    {
      latex: '\\text{B} \\to \\text{heart rot beet; Mo} \\to \\text{whiptail cauliflower}',
      desc: {
        english: '',
        hindi: 'बी हार्ट रोट बीट; मो व्हिपटेल फूलगोभी',
      },
    },
    {
      latex: 'N_2 + 8e^- + 8H^+ + 16\\,ATP \\to 2NH_3 + H_2',
      desc: {
        english: 'Nitrogenase',
        hindi: 'नाइट्रोजनेज़',
      },
    },
    {
      latex: '\\text{Leghaemoglobin protects nitrogenase from O}_2',
      desc: {
        english: '',
        hindi: 'लेगहीमोग्लोबिन नाइट्रोजनेज़ को O2 से बचाता है',
      },
    },
    {
      latex: '\\text{Nitrification: NH}_3 \\to NO_2^- (\\text{Nitrosomonas}) \\to NO_3^- (\\text{Nitrobacter})',
      desc: {
        english: '',
        hindi: 'नाइट्रिफिकेशन: NH 3 NO 2 - (नाइट्रोसोमोनास) NO 3 - (नाइट्रोबैक्टर)',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Element',
      problem: {
        english: 'Mg deficiency in plants causes:',
        hindi: 'पौधों में एमजी की कमी के कारण:',
      },
      answer: {
        english: 'Interveinal chlorosis in older leaves',
        hindi: 'पुरानी पत्तियों में इंटरवेनल क्लोरोसिस',
      },
      solution: {
        english: 'Mg is centre of chlorophyll; mobile element.',
        hindi: 'एमजी क्लोरोफिल का केंद्र है; मोबाइल तत्व.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Symbiotic',
      problem: {
        english: 'Symbiotic N-fixer in non-legume Alnus is:',
        hindi: 'गैर-फलियां एलनस में सहजीवी एन-फिक्सर है:',
      },
      answer: {
        english: 'Frankia',
        hindi: 'फ्रेंकिया',
      },
      solution: {
        english: 'Actinomycete in nodules of non-legumes.',
        hindi: 'गैर-फलियां के नोड्यूल में एक्टिनोमाइसेट।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Nitrogenase',
      problem: {
        english: 'Nitrogenase enzyme is protected from oxygen in legume nodules by:',
        hindi: 'नाइट्रोजनेज एंजाइम फलियों की गांठों में ऑक्सीजन से सुरक्षित रहता है:',
      },
      answer: {
        english: 'Leghaemoglobin',
        hindi: 'लेगहीमोग्लोबिन',
      },
      solution: {
        english: 'Pink-red O₂ scavenger.',
        hindi: 'गुलाबी-लाल O₂ मेहतर।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Trace',
      problem: {
        english: 'Whiptail disease of cauliflower is due to deficiency of:',
        hindi: 'फूलगोभी का व्हिपटेल रोग किसकी कमी के कारण होता है:',
      },
      answer: {
        english: 'Molybdenum (Mo)',
        hindi: 'मोलिब्डेनम (मो)',
      },
      solution: {
        english: 'Cofactor of nitrate reductase.',
        hindi: 'नाइट्रेट रिडक्टेस का सहकारक।',
      },
    },
    {
      year: 2018,
      exam: 'NEET',
      tag: 'Hydroponics',
      problem: {
        english: 'Hydroponics technique was developed by:',
        hindi: 'हाइड्रोपोनिक्स तकनीक किसके द्वारा विकसित की गई थी:',
      },
      answer: {
        english: 'Julius von Sachs',
        hindi: 'जूलियस वॉन सैक्स',
      },
      solution: {
        english: 'Identified essential elements (1860).',
        hindi: 'आवश्यक तत्वों की पहचान की गई (1860)।',
      },
    },
  ],
  next: {
    slug: 'chemical-coordination-integration',
    title: 'Chemical Coordination and Integration',
  },
};

export default chapter;
